import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { RestrictedGateway } from 'src/features/membershipType/types';
import { useListRestrictedGateway } from 'src/features/membershipType/api/useListRestrictedGateways';
import CreateRestrictedGatewayFormModal from 'src/features/membership/components/forms/restrictedGateway/CreateRestrictedGatewayFormModal';
import CellRestrictedGatewayActions from 'src/features/membership/components/cell/CellRestrictedGatewayActions';
import { restrictedGatewayMessages } from 'src/features/membershipType/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { Translation } from 'src/types';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import usePagination from 'src/registry/hooks/usePagination';
import { commonMessages } from 'src/utils/messages';

type Props = {
    membershipTypeId: string;
    inherited?: boolean;
};

const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale);

const RestrictedGatewaysTable = ({ inherited, membershipTypeId }: Props) => {
    const { formatMessage, locale } = useIntl();
    const manageMembershipType = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIP_TYPES]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();

    const { parkingId } = useParking();
    const { data: gatewayListData } = useListGateways({
        parkingId,
        restricted_membership_type: membershipTypeId,
    });

    const {
        data: restrictedGateways,
        isLoading,
        isError,
    } = useListRestrictedGateway({
        parkingId,
        membershipTypeId,
        ...queryFiltersOptions,
        ...querySearchOptions,
        ...queryPaginationOptions,
        config: { enabled: !!membershipTypeId },
    });

    const { data: membershipTemplate } = useGetMembershipType({
        parkingId,
        membershipTypeId,
    });
    const templateName = React.useMemo(() => {
        if (!membershipTemplate?.translations) return '-';
        return translate(membershipTemplate.translations, locale)?.name ?? '-';
    }, [membershipTemplate, locale]);

    const restrictedGatewaysIds = restrictedGateways?.results?.map((g) => g.id);

    const filteredEmptyGatewayTranslations = React.useMemo(() => {
        return gatewayListData?.results?.filter((gateway) => gateway.translations.length > 0) ?? [];
    }, [gatewayListData?.results]);

    const gatewayOptions = React.useMemo(
        () =>
            filteredEmptyGatewayTranslations?.map((gateway) => {
                const translatedGateway = gateway.translations.find((g) => g.language === locale);

                return { value: translatedGateway?.name, label: translatedGateway?.name };
            }),
        [filteredEmptyGatewayTranslations, locale],
    );

    const columnHelper = createColumnHelper<RestrictedGateway>();
    const columns = [
        columnHelper.accessor('translations', {
            enableHiding: true,
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: ({ row }) => {
                const gatewayName = row.original.translations?.find((gateway) => gateway.language === locale)?.name;
                const gatewayId = row.original.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/gateway/${gatewayId}`}
                        linkTitle={gatewayName}
                        hasPermissions={accessGateways}
                    />
                );
            },
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(restrictedGatewayMessages.gateway),
                options: gatewayOptions,
            },
            id: 'name',
        }),
        columnHelper.accessor('entry_zone', {
            enableHiding: true,
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const entryZoneName = row.original.entry_zone?.translations?.find((t) => t.language === locale)?.name;
                const entryZoneId = row.original.entry_zone?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/zone/${entryZoneId}`}
                        linkTitle={entryZoneName}
                        hasPermissions={accessZones}
                    />
                );
            },
            meta: {
                title: formatMessage(restrictedGatewayMessages.entryZone),
            },
        }),
        columnHelper.accessor('exit_zone', {
            enableHiding: true,
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const exitZoneName = row.original.exit_zone?.translations?.find((t) => t.language === locale)?.name;
                const exitZoneId = row.original.exit_zone?.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/zone/${exitZoneId}`}
                        linkTitle={exitZoneName}
                        hasPermissions={accessZones}
                    />
                );
            },
            meta: {
                title: formatMessage(restrictedGatewayMessages.exitZone),
            },
        }),
        ...(manageMembershipType
            ? [
                  columnHelper.display({
                      id: 'actions',
                      cell: (props) => (
                          <CellRestrictedGatewayActions restrictedGateways={restrictedGatewaysIds} {...props} />
                      ),
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<RestrictedGateway, any>
            pagination={pagination}
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={restrictedGateways}
            search={search}
            filters={filters}
            enableSearchParams={false}
            enableLocalFilters={false}
            semantics={{
                title: formatMessage(
                    inherited
                        ? restrictedGatewayMessages.inheritedRestrictedGateways
                        : restrictedGatewayMessages.restrictedGateways,
                    { entry: templateName },
                ),
                entryName: formatMessage(restrictedGatewayMessages.gateway).toLowerCase(),
                entryNames: formatMessage(restrictedGatewayMessages.gateways).toLowerCase(),
                initialColumnVisibility: { actions: !inherited },
                placeholder: formatMessage(commonMessages.name),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageMembershipType && !inherited && { newEntry: CreateRestrictedGatewayFormModal }),
            }}
        />
    );
};

export default RestrictedGatewaysTable;
